// CommandRow — per-column renderers for the command log. Row chrome
// (grid layout, hover, click, virtualization) lives in DataTable; this
// file only contributes the column schema, cell renderers, and the
// active-row className.

import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";

export const COMMAND_COLUMNS = [
    { id: "time", label: "Time", width: 72 },
    { id: "command", label: "Command", width: 200, flex: true },
    { id: "status", label: "Status", width: 64 },
    { id: "duration", label: "Duration", width: 64 },
];

// Context mode adds a small column on the far right showing the top
// other traces this command also matches. The trace listing's
// dim-badge pattern is the visual analog.
export const COMMAND_COLUMNS_CONTEXT = [
    { id: "time", label: "Time", width: 72 },
    { id: "command", label: "Command", width: 200, flex: true },
    { id: "status", label: "Status", width: 64 },
    { id: "duration", label: "Duration", width: 64 },
    { id: "context", label: "Also matches", width: 132 },
];

const MAX_CHIPS_PER_ROW = 3;

// Rendered inside a DataTable row, which is itself a <button> whenever the
// row is clickable — so this MUST NOT be a <button>: a button can't nest in
// a button (invalid HTML + a React hydration error). Use the same
// span[role="button"] keyboard-operable pattern DataTable uses for its
// sortable column headers.
function TraceChip({ traceId, summary, probability, onOpen }) {
    const short = String(traceId).replace(/-/g, "").slice(0, 4);
    const pct = Math.round((probability || 0) * 100);
    const open = (e) => { e.stopPropagation(); onOpen?.(traceId); };
    return (
        <span
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open(e);
                }
            }}
            title={summary ? `${summary} · ${pct}%` : `${traceId} · ${pct}%`}
            className="inline-flex select-none items-center rounded-[2px] border border-[color:var(--line-soft)] bg-transparent px-[4px] py-0 text-[9px] leading-[14px] tabular-nums font-medium text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
        >
            {short}
        </span>
    );
}

export function renderCommandCell(command, col, ctx = {}) {
    const ok = command.exitCode === 0;
    const { contextActive, focalTraceId, attributionEntry, traceLookup, onOpenTrace } = ctx;
    switch (col.id) {
        case "time":
            return (
                <div className="grid gap-0">
                    <strong className="text-[10px] leading-[1.12] font-medium">
                        {timeLabelFormatter.format(command.timestamp)}
                    </strong>
                    <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">
                        {dayLabelFormatter.format(command.timestamp)}
                    </span>
                </div>
            );
        case "command":
            return (
                <div className="flex min-w-0 items-center gap-[6px]">
                    <span
                        className={[
                            "h-[6px] w-[6px] flex-none rounded-[999px]",
                            ok ? "bg-emerald-400" : "bg-red-400",
                        ].join(" ")}
                    />
                    <strong className="truncate text-[11px] leading-[1.08] font-medium">
                        {command.command}
                    </strong>
                </div>
            );
        case "status":
            return (
                <span
                    className={[
                        "justify-self-end text-[10px] leading-[1.08] font-medium",
                        ok ? "text-[color:var(--muted)]" : "text-red-400",
                    ].join(" ")}
                >
                    exit {command.exitCode}
                </span>
            );
        case "duration":
            return (
                <span className="justify-self-end text-[10px] leading-[1.08] text-[color:var(--muted)] font-medium">
                    {command.durationMs}ms
                </span>
            );
        case "context": {
            if (!contextActive || !attributionEntry) {
                return <span aria-hidden="true" />;
            }
            // Show up to N other-trace chips (excluding the focal trace).
            // `links` already arrives sorted by probability desc.
            const others = attributionEntry.links.filter((l) => l.traceId !== focalTraceId);
            if (!others.length) return <span aria-hidden="true" />;
            const top = others.slice(0, MAX_CHIPS_PER_ROW);
            const overflow = Math.max(0, others.length - top.length);
            return (
                <div className="flex min-w-0 flex-nowrap items-center justify-end gap-[3px] overflow-hidden">
                    {top.map((link) => {
                        const t = traceLookup?.get?.(link.traceId);
                        return (
                            <TraceChip
                                key={link.traceId}
                                traceId={link.traceId}
                                summary={t?.summary || ""}
                                probability={link.probability}
                                onOpen={onOpenTrace}
                            />
                        );
                    })}
                    {overflow > 0 && (
                        <span
                            className="flex-none text-[9px] leading-[14px] tabular-nums text-[color:var(--muted-soft)]"
                            title={`${overflow} more`}
                        >
                            +{overflow}
                        </span>
                    )}
                </div>
            );
        }
        default:
            return null;
    }
}

export function commandRowClassName({ active }) {
    return active ? "!bg-[color:var(--green-soft)] shadow-[inset_2px_0_0_var(--green)]" : "";
}
